// src/services/leadershipEngine/scoreRepository.js
import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore';

import { db } from '../firebase';
import { EVENT_TYPES, SCORE } from './score.config';

/* ===========================
   Helpers de data
=========================== */
function tsNow() {
  return Timestamp.fromDate(new Date());
}

function tsMonthsAgo(months) {
  const d = new Date();
  d.setMonth(d.getMonth() - Number(months || 0));
  return Timestamp.fromDate(d);
}

function toMillis(v) {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toMillis();
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string') {
    const d = new Date(v.includes('T') ? v : `${v}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  return null;
}

function monthKeyFromMillis(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function toTimestamp(v) {
  if (!v) return tsNow();
  if (v instanceof Timestamp) return v;
  if (typeof v?.toDate === 'function') return v; // Timestamp-like
  if (v instanceof Date) return Timestamp.fromDate(v);
  const ms = toMillis(v);
  return ms ? Timestamp.fromMillis(ms) : tsNow();
}

/* ===========================
   SCORE Snapshot (FONTE OFICIAL)
   Coleção: score/{uid}
=========================== */
export function scoreDocRef(uid) {
  if (!uid) throw new Error('UID não informado.');
  return doc(db, 'score', uid);
}

export async function getScoreSnapshot(uid) {
  if (!uid) return null;
  const ref = scoreDocRef(uid);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

export async function saveScoreSnapshot(uid, payload) {
  if (!uid) throw new Error('UID não informado.');
  const ref = scoreDocRef(uid);

  await setDoc(
    ref,
    {
      ...(payload || {}),
      updatedAt: tsNow(),
    },
    { merge: true }
  );
}

/**
 * ✅ Realtime (Sidebar e lugares que precisam ao vivo)
 */
export function listenScoreSnapshot(uid, cb, onError) {
  if (!uid) throw new Error('UID não informado.');
  const ref = scoreDocRef(uid);
  return onSnapshot(
    ref,
    (snap) => cb(snap.exists() ? snap.data() : null),
    (err) => (typeof onError === 'function' ? onError(err) : null)
  );
}

/* ===========================
   Vendas (rolling 12m)
   ⚠️ compatível com LEGACY e 6K
=========================== */
export async function listVendas12m(uid) {
  if (!uid) throw new Error('UID não informado.');

  // ✅ 6K: usa ROLLING_MONTHS (se existir), senão cai no legacy WINDOW_MONTHS
  const months =
    Number(SCORE?.ROLLING_MONTHS) || Number(SCORE?.RENDA?.WINDOW_MONTHS) || 12;

  const start = tsMonthsAgo(months);

  try {
    const qy = query(
      collection(db, 'vendas'),
      where('uid', '==', uid),
      where('data', '>=', start),
      orderBy('data', 'desc')
    );
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    // fallback: sem índice composto (filtra localmente)
    const q2 = query(collection(db, 'vendas'), where('uid', '==', uid));
    const snap = await getDocs(q2);

    const startMs = start.toMillis();
    const endMs = tsNow().toMillis();

    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((v) => {
        const ms = toMillis(
          v?.data || v?.dataVenda || v?.createdAt || v?.updatedAt
        );
        return ms && ms >= startMs && ms <= endMs;
      })
      .sort(
        (a, b) =>
          (toMillis(b?.data || b?.dataVenda) || 0) -
          (toMillis(a?.data || a?.dataVenda) || 0)
      );
  }
}

export function mesesComVendaFromVendas(vendas12m) {
  const months = new Set();
  (vendas12m || []).forEach((v) => {
    const ms = toMillis(
      v?.data || v?.dataVenda || v?.createdAt || v?.updatedAt
    );
    if (ms) months.add(monthKeyFromMillis(ms));
  });
  return Array.from(months).sort();
}

/**
 * ✅ NOVO — listar vendas por range (mês atual, comparativos etc.)
 * - usado pelo Score 6K (regras mensais)
 */
export async function listVendasByRange(uid, fromDate, toDate) {
  if (!uid) throw new Error('UID não informado.');
  const de = toTimestamp(fromDate);
  const ate = toTimestamp(toDate);

  try {
    const qy = query(
      collection(db, 'vendas'),
      where('uid', '==', uid),
      where('data', '>=', de),
      where('data', '<=', ate)
    );
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    // fallback: sem índice composto (filtra localmente)
    const q2 = query(collection(db, 'vendas'), where('uid', '==', uid));
    const snap = await getDocs(q2);

    const startMs = de.toMillis();
    const endMs = ate.toMillis();

    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((v) => {
        const ms = toMillis(
          v?.data || v?.dataVenda || v?.createdAt || v?.updatedAt
        );
        return ms && ms >= startMs && ms <= endMs;
      })
      .sort(
        (a, b) =>
          (toMillis(b?.data || b?.dataVenda) || 0) -
          (toMillis(a?.data || a?.dataVenda) || 0)
      );
  }
}

/* ===========================
   Eventos Score
=========================== */
export async function listEventosScore(uid, daysBack = 30) {
  if (!uid) throw new Error('UID não informado.');
  const start = Timestamp.fromDate(new Date(Date.now() - daysBack * 86400000));

  try {
    const qy = query(
      collection(db, 'eventos_score'),
      where('uid', '==', uid),
      where('createdAt', '>=', start),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    // fallback: sem índice composto (filtra localmente)
    const q2 = query(collection(db, 'eventos_score'), where('uid', '==', uid));
    const snap = await getDocs(q2);

    const startMs = start.toMillis();
    const endMs = tsNow().toMillis();

    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((ev) => {
        const ms = toMillis(ev?.createdAt);
        return ms && ms >= startMs && ms <= endMs;
      })
      .sort(
        (a, b) => (toMillis(b?.createdAt) || 0) - (toMillis(a?.createdAt) || 0)
      );
  }
}

/**
 * ✅ NOVO — listar eventos_score por range (mês atual)
 * - usado pelo Score 6K (regras mensais)
 */
export async function listEventosScoreByRange(uid, fromDate, toDate) {
  if (!uid) throw new Error('UID não informado.');
  const de = toTimestamp(fromDate);
  const ate = toTimestamp(toDate);

  try {
    const qy = query(
      collection(db, 'eventos_score'),
      where('uid', '==', uid),
      where('createdAt', '>=', de),
      where('createdAt', '<=', ate),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(qy);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    // fallback: sem índice composto
    const q2 = query(collection(db, 'eventos_score'), where('uid', '==', uid));
    const snap = await getDocs(q2);

    const startMs = de.toMillis();
    const endMs = ate.toMillis();

    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((ev) => {
        const ms = toMillis(ev?.createdAt);
        return ms && ms >= startMs && ms <= endMs;
      })
      .sort(
        (a, b) => (toMillis(b?.createdAt) || 0) - (toMillis(a?.createdAt) || 0)
      );
  }
}

/* ===========================
   Contagem por tipo
=========================== */
export function countsFromEventos(eventos) {
  const counts = { imoveis: 0, negocios: 0, visitas: 0, vendas: 0 };

  (eventos || []).forEach((ev) => {
    switch (ev?.tipo) {
      case EVENT_TYPES.IMOVEL_CADASTRADO:
        counts.imoveis++;
        break;
      case EVENT_TYPES.NEGOCIO_CADASTRADO:
        counts.negocios++;
        break;
      case EVENT_TYPES.VISITA_REALIZADA:
        counts.visitas++;
        break;
      case EVENT_TYPES.VENDA_CADASTRADA:
        counts.vendas++;
        break;
      default:
        break;
    }
  });

  return counts;
}

/* ===========================
   Placas (modelo atual)
   - "continua do jeito que está": imoveis.temPlaca === true
   - conta apenas as placas ativas do corretor (e sócios se existirem? aqui: só o uid)
   - Se quiser incluir sócios no score, me fala que eu adapto
=========================== */
export async function countPlacasAtivas(uid) {
  if (!uid) throw new Error('UID não informado.');

  try {
    const qy = query(
      collection(db, 'imoveis'),
      where('ownerId', '==', uid),
      where('temPlaca', '==', true)
    );
    const snap = await getDocs(qy);

    // Se quiser filtrar "disponível para venda" aqui também, precisa da mesma
    // função isDisponivelParaVenda do dashboardService (ou duplicar).
    // Por enquanto, como você pediu "do jeito que está", conta temPlaca true.
    return snap.size;
  } catch (err) {
    console.error('Erro em countPlacasAtivas:', err);
    return 0;
  }
}

/* ===========================
   Última atividade (legacy delta / regra 7 dias)
   - mantido para compatibilidade
=========================== */
export async function getLastActivityByType(uid) {
  if (!uid) throw new Error('UID não informado.');

  async function lastEventOf(tipo) {
    try {
      const qy = query(
        collection(db, 'eventos_score'),
        where('uid', '==', uid),
        where('tipo', '==', tipo),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      const snap = await getDocs(qy);
      return snap.empty ? null : (snap.docs[0].data()?.createdAt ?? null);
    } catch {
      // fallback: sem índice composto
      const q2 = query(
        collection(db, 'eventos_score'),
        where('uid', '==', uid)
      );
      const snap = await getDocs(q2);
      const all = snap.docs.map((d) => d.data());
      return (
        all
          .filter((x) => x?.tipo === tipo)
          .sort(
            (a, b) =>
              (toMillis(b?.createdAt) || 0) - (toMillis(a?.createdAt) || 0)
          )[0]?.createdAt ?? null
      );
    }
  }

  const [lastImovelAt, lastNegocioAt, lastVisitaAt] = await Promise.all([
    lastEventOf(EVENT_TYPES.IMOVEL_CADASTRADO),
    lastEventOf(EVENT_TYPES.NEGOCIO_CADASTRADO),
    lastEventOf(EVENT_TYPES.VISITA_REALIZADA),
  ]);

  return { lastImovelAt, lastNegocioAt, lastVisitaAt };
}
